import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize, forkJoin } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { DownloadService } from 'src/app/services/download.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type CertificateStatus = 'Audit' | 'Audited' | 'Back' | '';
type CertificationFile = {
  id?: string;
  url?: string;
  name: string;
  sizeText?: string;
};

@Component({
  selector: 'app-team-certification-audit',
  standalone: true,
  templateUrl: './team-certification-audit.component.html',
  styleUrls: ['./team-certification-audit.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NzCardModule,
    NzTableModule,
    NzSpinModule,
    NzTagModule,
    NzSelectModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzFormModule,
    NzDropDownModule,
    NzModalModule,
    NzPaginationModule,
    I18nPipe,
    TestIdDirective
  ]
})
export class TeamCertificationAuditComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);

  @ViewChild('detailTpl', { static: true }) detailTpl!: TemplateRef<{ $implicit: any }>;
  @ViewChild('detailFooterTpl', { static: true }) detailFooterTpl!: TemplateRef<{ $implicit: NzModalRef }>;
  @ViewChild('rejectReasonTpl', { static: true }) rejectReasonTpl!: TemplateRef<{ $implicit: NzModalRef }>;
  @ViewChild('rejectFooterTpl', { static: true }) rejectFooterTpl!: TemplateRef<{ $implicit: NzModalRef }>;

  loading = false;
  list: any[] = [];
  total = 0;
  pageIndex = 1;
  pageSize = 20;

  statusFilter: CertificateStatus | 'All' = 'All';
  keyword = '';

  pendingCount = 0;
  approvedCount = 0;
  rejectedCount = 0;
  currentDetailStatus: CertificateStatus = '';
  currentDetailRow: any = null;
  rejectReason = '';
  rejectReasonTouched = false;
  rejectTenantName = '';
  rejectingRow: any = null;
  private detailModalRef: NzModalRef | null = null;
  private rejectModalRef: NzModalRef | null = null;
  private suppressLoadingFinalize = false;

  ngOnInit(): void {
    this.load(true);
  }

  formatDateTime(value: unknown): string {
    if (value === null || value === undefined) return '';
    const format = (d: Date): string => {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) {
      const ts = num < 1000000000000 ? num * 1000 : num;
      const d = new Date(ts);
      if (!Number.isFinite(d.getTime())) return '';
      return format(d);
    }
    const parsed = Date.parse(String(value));
    if (!Number.isFinite(parsed)) return '';
    const d = new Date(parsed);
    if (!Number.isFinite(d.getTime())) return '';
    return format(d);
  }

  getStatusLabel(status: CertificateStatus): string {
    if (!status) return this.i18n.fanyi('team.cert.audit.status.none');
    if (status === 'Audit') return this.i18n.fanyi('team.cert.audit.status.audit');
    if (status === 'Audited') return this.i18n.fanyi('team.cert.audit.status.audited');
    return this.i18n.fanyi('team.cert.audit.status.back');
  }

  getStatusTagColor(status: CertificateStatus): string {
    if (!status) return 'default';
    if (status === 'Audit') return 'processing';
    if (status === 'Audited') return 'success';
    return 'error';
  }

  onSearch(): void {
    this.pageIndex = 1;
    this.load(true);
  }

  onReset(): void {
    this.statusFilter = 'All';
    this.keyword = '';
    this.pageIndex = 1;
    this.load(true);
  }

  onPageIndexChange(index: number): void {
    this.pageIndex = index;
    this.load(false);
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 1;
    this.load(true);
  }

  private toCount(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  private extractStatusCounts(data: any): { pending: number; approved: number; rejected: number } | null {
    if (!data) return null;

    const directPending = data.pendingCount ?? data.auditCount ?? data.waitCount;
    const directApproved = data.approvedCount ?? data.auditedCount ?? data.successCount;
    const directRejected = data.rejectedCount ?? data.backCount ?? data.failCount;
    if (directPending !== undefined || directApproved !== undefined || directRejected !== undefined) {
      return {
        pending: this.toCount(directPending),
        approved: this.toCount(directApproved),
        rejected: this.toCount(directRejected)
      };
    }

    const mapCandidate =
      data.certificateStatusCount ??
      data.certificateStatusCounts ??
      data.certificateStatusCountMap ??
      data.statusCount ??
      data.statusCounts ??
      data.statusCountMap ??
      data.countMap ??
      data.counts ??
      data.statistics ??
      data.statistic;

    if (mapCandidate && typeof mapCandidate === 'object') {
      const pending = mapCandidate.Audit ?? mapCandidate.audit ?? mapCandidate.pending;
      const approved = mapCandidate.Audited ?? mapCandidate.audited ?? mapCandidate.approved;
      const rejected = mapCandidate.Back ?? mapCandidate.back ?? mapCandidate.rejected ?? mapCandidate.fail;
      if (pending !== undefined || approved !== undefined || rejected !== undefined) {
        return {
          pending: this.toCount(pending),
          approved: this.toCount(approved),
          rejected: this.toCount(rejected)
        };
      }
    }

    return null;
  }

  private applyCountsFromResponseData(data: any): boolean {
    const counts = this.extractStatusCounts(data);
    if (!counts) return false;
    this.pendingCount = counts.pending;
    this.approvedCount = counts.approved;
    this.rejectedCount = counts.rejected;
    this.cdr.markForCheck();
    return true;
  }

  private loadCounts(): void {
    const base: any = {
      pageIndex: 0,
      pageSize: 1,
      direction: 'desc',
      refreshTime: Date.now(),
      type: 'Common'
    };
    this.http.post(ConstantUtil.ADMIN_TENANT_CERTIFICATE_LIST, base).subscribe(res => {
      if (res?.success && this.applyCountsFromResponseData(res?.data)) return;

      forkJoin({
        pending: this.http.post(ConstantUtil.ADMIN_TENANT_CERTIFICATE_LIST, { ...base, certificateStatus: 'Audit' }),
        approved: this.http.post(ConstantUtil.ADMIN_TENANT_CERTIFICATE_LIST, { ...base, certificateStatus: 'Audited' }),
        rejected: this.http.post(ConstantUtil.ADMIN_TENANT_CERTIFICATE_LIST, { ...base, certificateStatus: 'Back' })
      }).subscribe(r => {
        const pending = r.pending?.success ? this.toCount(r.pending?.data?.totalCount ?? 0) : 0;
        const approved = r.approved?.success ? this.toCount(r.approved?.data?.totalCount ?? 0) : 0;
        const rejected = r.rejected?.success ? this.toCount(r.rejected?.data?.totalCount ?? 0) : 0;
        this.pendingCount = pending;
        this.approvedCount = approved;
        this.rejectedCount = rejected;
        this.cdr.markForCheck();
      });
    });
  }

  load(includeCounts: boolean): void {
    this.loading = true;
    const body: any = {
      pageIndex: this.pageIndex - 1,
      pageSize: this.pageSize,
      direction: 'desc',
      refreshTime: Date.now(),
      type: 'Common'
    };
    const key = this.keyword.trim();
    if (key) body.key = key;
    if (this.statusFilter !== 'All') body.certificateStatus = this.statusFilter;
    this.http
      .post(ConstantUtil.ADMIN_TENANT_CERTIFICATE_LIST, body)
      .pipe(
        finalize(() => {
          if (!this.suppressLoadingFinalize) this.loading = false;
          this.suppressLoadingFinalize = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(res => {
        if (!res?.success) {
          this.message.error(res?.msg || this.i18n.fanyi('team.cert.audit.load-failed'));
          return;
        }
        const listWrap = res.data?.list ?? res.data;
        const items = Array.isArray(listWrap?.items) ? listWrap.items : [];
        this.list = items.map((item: any) => this.enrichListItem(item));
        this.total = Number(listWrap?.totalCount ?? res.data?.totalCount ?? items.length);
        if (includeCounts && !this.applyCountsFromResponseData(res.data)) this.loadCounts();
        this.cdr.markForCheck();
      });
  }

  private resolveTenantId(row: any): string {
    const candidate =
      row?.tenantId ??
      row?.tenantID ??
      row?.tenant_id ??
      row?.tenant?.id ??
      row?.tenantModel?.id ??
      row?.tenant?.tenantId ??
      row?.tenantModel?.tenantId ??
      row?.id;
    return String(candidate ?? '').trim();
  }

  private resolveTenantName(row: any): string {
    const candidate =
      row?.name ??
      row?.tenantName ??
      row?.tenant_name ??
      row?.tenant?.name ??
      row?.tenantModel?.name ??
      row?.companyName ??
      row?.enterpriseName;
    return String(candidate ?? '').trim();
  }

  private resolveTenantShortName(row: any): string {
    const candidate = row?.shortName ?? row?.tenantShortName ?? row?.short_name ?? row?.tenant?.shortName ?? row?.tenantModel?.shortName;
    return String(candidate ?? '').trim();
  }

  private resolveCertificateType(src: any): string {
    if (!src || typeof src !== 'object') return '';
    const candidate =
      src.certificateTypeName ?? src.certificateType ?? src.certType ?? src.certificateCategory ?? src.certificateName ?? src.certName;
    return String(candidate ?? '').trim();
  }

  private resolveCertificateOrg(src: any): string {
    if (!src || typeof src !== 'object') return '';
    const candidate =
      src.certificateOrg ??
      src.certificateOrganization ??
      src.certificateAuthority ??
      src.certOrg ??
      src.authOrg ??
      src.issuingAuthority ??
      src.issuerName;
    return String(candidate ?? '').trim();
  }

  private enrichListItem(item: any): any {
    if (!item || typeof item !== 'object') return item;
    const name = this.resolveTenantName(item);
    const shortName = this.resolveTenantShortName(item);
    const unifiedNumber = String(
      item?.unifiedNumber ?? item?.unified_number ?? item?.tenant?.unifiedNumber ?? item?.tenantModel?.unifiedNumber ?? ''
    ).trim();
    const representativeNumber = String(
      item?.representativeNumber ??
        item?.representative_number ??
        item?.tenant?.representativeNumber ??
        item?.tenantModel?.representativeNumber ??
        ''
    ).trim();
    return {
      ...item,
      name: item?.name ?? (name || undefined),
      shortName: item?.shortName ?? (shortName || undefined),
      unifiedNumber: item?.unifiedNumber ?? (unifiedNumber || undefined),
      representativeNumber: item?.representativeNumber ?? (representativeNumber || undefined)
    };
  }

  formatFileSize(value: unknown): string {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1) {
      size /= 1024;
      idx += 1;
    }
    const fixed = idx === 0 ? String(Math.round(size)) : size.toFixed(1);
    return `${fixed} ${units[idx]}`;
  }

  private normalizeFiles(...sources: any[]): CertificationFile[] {
    const files: CertificationFile[] = [];
    const pushFile = (raw: any) => {
      if (!raw) return;
      const id = String(raw.id ?? raw.fileId ?? raw.attachmentId ?? raw.file?.id ?? '').trim();
      const url = String(raw.previewUrl ?? raw.url ?? raw.downloadUrl ?? raw.fileUrl ?? '').trim();
      const name = String(raw.name ?? raw.fileName ?? raw.originalName ?? raw.filename ?? '').trim();
      const sizeText = this.formatFileSize(raw.size ?? raw.fileSize ?? raw.length ?? raw.byteSize);
      if (!name && !id && !url) return;
      files.push({
        id: id || undefined,
        url: url || undefined,
        name: name || this.i18n.fanyi('download.default-filename'),
        sizeText: sizeText || undefined
      });
    };

    for (const src of sources) {
      const list =
        src?.files ??
        src?.fileList ??
        src?.certificateFiles ??
        src?.certificateFile ??
        src?.certFiles ??
        src?.attachments ??
        src?.attachmentList ??
        src?.certificateFileList ??
        src?.certificateFileItems ??
        src?.certificateAttachments ??
        src?.certificateAttachmentList;
      if (Array.isArray(list)) list.forEach(pushFile);
      else if (list && typeof list === 'object') pushFile(list);
      if (src?.certificateFileId) {
        const fileId = String(src.certificateFileId ?? '').trim();
        if (fileId && !files.some(f => f.id === fileId)) {
          pushFile({ fileId, fileName: src.certificateFileName, fileSize: src.certificateFileSize });
        }
      }
      const idList =
        src?.certificateFileIds ??
        src?.certificateFileIdList ??
        src?.certificateFileIDList ??
        src?.fileIds ??
        src?.fileIdList ??
        src?.attachmentIds ??
        src?.attachmentIdList;
      if (Array.isArray(idList)) {
        idList
          .map((v: any) => String(v ?? '').trim())
          .filter(Boolean)
          .forEach((id: string, idx: number) => {
            pushFile({ fileId: id, fileName: `${this.i18n.fanyi('download.default-filename')}-${idx + 1}` });
          });
      }
    }

    return files;
  }

  previewFile(file: CertificationFile): void {
    const url = String(file.url || '').trim();
    if (!url) {
      this.message.error(this.i18n.fanyi('team.cert.audit.preview-url-missing') || '找不到预览链接');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  view(row: any): void {
    const tenantId = this.resolveTenantId(row);
    if (!tenantId) return;
    this.loading = true;
    this.cdr.markForCheck();
    this.http
      .post(`${ConstantUtil.ADMIN_TENANT_ITEM}/${tenantId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(res => {
        const safeRes: any = res || {};
        const data: any = safeRes.data ?? safeRes;
        const tenantModel: any = data.tenantModel ?? data.tenant ?? data.tenantInfo ?? null;
        const enrichedRow = this.enrichListItem(row);
        const directFileId = String(tenantModel?.certificateFileId ?? data?.certificateFileId ?? '').trim();
        const directFileName = String(data?.certificateFileName ?? tenantModel?.certificateFileName ?? '').trim();
        const directFileSize = data?.certificateFileSize ?? tenantModel?.certificateFileSize;
        const directPreviewUrl = String(data?.certificateFileViewUrl ?? '').trim();
        const files =
          directFileId && directFileName
            ? [
                {
                  id: directFileId,
                  url: directPreviewUrl || undefined,
                  name: directFileName,
                  sizeText: this.formatFileSize(directFileSize) || undefined
                }
              ]
            : this.normalizeFiles(data, tenantModel, enrichedRow);

        const tenantName =
          tenantModel?.name ?? enrichedRow?.tenantName ?? enrichedRow?.name ?? this.resolveTenantName(tenantModel ?? {}) ?? '';

        const certificateStatus = (tenantModel?.certificateStatus ?? enrichedRow?.certificateStatus ?? '') as CertificateStatus;

        const unifiedNumber = String(tenantModel?.unifiedNumber ?? enrichedRow?.unifiedNumber ?? enrichedRow?.unified_number ?? '').trim();

        const representativeNumber = String(
          tenantModel?.representativeNumber ?? enrichedRow?.representativeNumber ?? enrichedRow?.representative_number ?? ''
        ).trim();

        const submittedAt =
          tenantModel?.certificateTime ??
          tenantModel?.certificateSubmitTime ??
          tenantModel?.submitTime ??
          tenantModel?.submittedAt ??
          enrichedRow?.certificateTime ??
          enrichedRow?.certificateSubmitTime ??
          enrichedRow?.submitTime ??
          enrichedRow?.submittedAt ??
          tenantModel?.updateTime ??
          enrichedRow?.updateTime;

        const certificateType = this.resolveCertificateType(tenantModel) || this.resolveCertificateType(enrichedRow);
        const certificateOrg = this.resolveCertificateOrg(tenantModel) || this.resolveCertificateOrg(enrichedRow);
        const failReason = String(
          tenantModel?.certificateFailReason ?? data?.certificateFailReason ?? enrichedRow?.certificateFailReason ?? ''
        ).trim();

        this.currentDetailStatus = certificateStatus;
        this.currentDetailRow = enrichedRow;

        this.detailModalRef = this.modal.create({
          nzTitle: this.i18n.fanyi('team.cert.audit.detail.title'),
          nzContent: this.detailTpl,
          nzData: {
            row: enrichedRow,
            tenant: tenantModel,
            files,
            tenantName,
            status: certificateStatus,
            certificateType,
            certificateOrg,
            submittedAt,
            unifiedNumber,
            representativeNumber,
            failReason
          },
          nzFooter: this.detailFooterTpl,
          nzWrapClassName: 'team-cert-audit-detail-modal',
          nzWidth: 720
        });
      });
  }

  approve(row: any): void {
    if ((row?.certificateStatus ?? '') !== 'Audit') return;
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('team.cert.audit.approve.title'),
      nzContent: this.i18n.fanyi('team.cert.audit.approve.content'),
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () => this.updateStatus(row, 'Audited')
    });
  }

  reject(row: any): void {
    if ((row?.certificateStatus ?? '') !== 'Audit') return;
    this.openRejectReasonModal(row);
  }

  closeDetailModal(): void {
    this.detailModalRef?.close();
    this.detailModalRef = null;
  }

  private updateStatus(row: any, status: Exclude<CertificateStatus, ''>, reason?: string): Promise<boolean> {
    const tenantId = this.resolveTenantId(row);
    if (!tenantId) return Promise.resolve(false);
    return new Promise(resolve => {
      this.loading = true;
      this.cdr.markForCheck();
      const apiPathResolved =
        status === 'Audited'
          ? ConstantUtil.TENANT_V1_CERTIFICATE_SUCCESS.replace('{id}', String(tenantId))
          : ConstantUtil.TENANT_V1_CERTIFICATE_FAIL;
      const body = status === 'Audited' ? {} : { tenantId, reason: reason ?? '' };
      this.http
        .post(apiPathResolved, body)
        .pipe(
          finalize(() => {
            if (!this.suppressLoadingFinalize) this.loading = false;
            this.suppressLoadingFinalize = false;
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: res => {
            if (res?.success) {
              row.certificateStatus = status;
              this.message.success(this.i18n.fanyi('common.update-success'));
              this.suppressLoadingFinalize = true;
              this.pageIndex = 1;
              this.load(true);
              resolve(true);
              return;
            }
            this.message.error(res?.msg || this.i18n.fanyi('team.cert.audit.update-failed'));
            resolve(false);
          },
          error: () => {
            this.message.error(this.i18n.fanyi('team.cert.audit.update-failed'));
            resolve(false);
          }
        });
    });
  }

  private openRejectReasonModal(row: any): void {
    const enrichedRow = this.enrichListItem(row);
    this.rejectReason = '';
    this.rejectReasonTouched = false;
    this.rejectingRow = enrichedRow;
    this.rejectTenantName = this.resolveTenantName(enrichedRow) || enrichedRow?.name || '';
    this.rejectModalRef = this.modal.create({
      nzTitle: this.i18n.fanyi('team.cert.audit.reject.title'),
      nzContent: this.rejectReasonTpl,
      nzFooter: this.rejectFooterTpl,
      nzWidth: 520
    });
  }

  onRejectCancel(): void {
    this.rejectModalRef?.close();
    this.rejectModalRef = null;
  }

  submitRejectReason(): void {
    this.rejectReasonTouched = true;
    const reason = this.rejectReason.trim();
    if (!reason || !this.rejectingRow) return;
    this.updateStatus(this.rejectingRow, 'Back', reason).then(success => {
      if (success && this.rejectModalRef) {
        this.rejectModalRef.close();
        this.rejectModalRef = null;
      }
    });
  }
}
